import { z } from "zod";
import type { PlanId } from "./billing";
import { TEXT_LIMITS } from "./decks";

/**
 * Writing questions with AI (packages/ai): presenter → API → model, and back as draft
 * slides the editor shows before anything is added to the deck.
 */

export const AI_STYLES = ["quiz", "poll", "mixed"] as const;
export type AiStyle = (typeof AI_STYLES)[number];

/**
 * How many times an organisation may ask in a day, by plan. Each ask costs money; Free
 * gets enough to try it, the paid plans enough for a day's preparation.
 */
export const AI_DAILY_LIMIT: Record<PlanId, number> = { free: 3, starter: 20, pro: 30, business: 50, enterprise: 100 };

export const aiWriteRequestSchema = z
  .object({
    /** A topic, or notes pasted from a talk or lesson. */
    brief: z.string().trim().min(3).max(6000),
    count: z.number().int().min(3).max(15),
    style: z.enum(AI_STYLES),
    /** The presenter's language, for the questions. */
    language: z.string().trim().min(2).max(10).default("en"),
  })
  .strict();
export type AiWriteRequest = z.infer<typeof aiWriteRequestSchema>;

/** One written question, checked: whatever the model sends back is held to this. */
export const writtenSlideSchema = z
  .object({
    type: z.enum(["mcq", "true_false", "poll", "word_cloud", "open_text"]),
    prompt: z.string().trim().min(1).max(TEXT_LIMITS.question),
    options: z.array(z.string().trim().min(1).max(TEXT_LIMITS.option)).max(10),
    /** mcq: the right option's position; true_false: 0 true, 1 false; otherwise -1. */
    correct: z.number().int().min(-1).max(9),
  })
  .strict();
export type WrittenSlideDraft = z.infer<typeof writtenSlideSchema>;

export const aiWriteResultSchema = z.object({
  slides: z.array(writtenSlideSchema),
  remainingToday: z.number().int().nonnegative(),
});
export type AiWriteResult = z.infer<typeof aiWriteResultSchema>;

/** Whether the editor offers it, and how many asks are left today. */
export const aiStatusSchema = z.object({
  available: z.boolean(),
  remainingToday: z.number().int().nonnegative(),
  dailyLimit: z.number().int().nonnegative(),
});
export type AiStatus = z.infer<typeof aiStatusSchema>;
